import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/auth/AuthProvider";
import { useRole } from "@/hooks/useRole";
import { fetchGames } from "@/services/gameService";
import { createCommunity } from "@/services/mentorCommunityService";
import { Game } from "@/types/game";

const CriarComunidade = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { authUser } = useRole();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [games, setGames] = useState<Game[]>([]);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadMode, setUploadMode] = useState<'upload' | 'url'>('upload');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    game_id: '',
    is_featured: false,
    rules: ''
  });

  useEffect(() => {
    loadGames();
  }, []);

  const loadGames = async () => {
    try {
      const gamesData = await fetchGames();
      setGames(gamesData);
    } catch (error) {
      console.error('Erro ao carregar jogos:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Erro",
        description: "Por favor, selecione apenas arquivos de imagem.",
        variant: "destructive",
      });
      return;
    }

    // Validar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Erro",
        description: "A imagem deve ter no máximo 5MB.",
        variant: "destructive",
      });
      return;
    }

    setImageFile(file);
    
    // Criar preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUrlChange = (url: string) => {
    setImageUrl(url);
    setImagePreview(url);
  };

  const removeImage = () => {
    setImageFile(null);
    setImageUrl('');
    setImagePreview('');
  };

  const uploadImageToSupabase = async (file: File): Promise<string> => {
    const { supabase } = await import('@/integrations/supabase/client');
    
    // Gerar nome único para o arquivo
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2);
    const fileExtension = file.name.split('.').pop();
    const fileName = `community-covers/${timestamp}-${randomId}.${fileExtension}`;

    try {
      console.log('📤 [uploadImageToSupabase] Tentando upload para bucket games...', fileName);

      // Tentar usar o bucket 'games' que já existe e tem permissões
      const { data, error } = await supabase.storage
        .from('games')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('❌ [uploadImageToSupabase] Erro no upload para games:', error);
        
        // Tentar bucket alternativo 'profile-images'
        console.log('📤 [uploadImageToSupabase] Tentando bucket profile-images...');
        const { data: data2, error: error2 } = await supabase.storage
          .from('profile-images')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error2) {
          console.error('❌ [uploadImageToSupabase] Erro no upload para profile-images:', error2);
          throw new Error('Falha no upload para ambos os buckets');
        }

        // Obter URL pública do bucket profile-images
        const { data: urlData2 } = supabase.storage
          .from('profile-images')
          .getPublicUrl(fileName);

        console.log('✅ [uploadImageToSupabase] Upload realizado com sucesso no profile-images');
        return urlData2.publicUrl;
      }

      // Obter URL pública do bucket games
      const { data: urlData } = supabase.storage
        .from('games')
        .getPublicUrl(fileName);

      console.log('✅ [uploadImageToSupabase] Upload realizado com sucesso no games');
      return urlData.publicUrl;

    } catch (error) {
      console.error('❌ [uploadImageToSupabase] Erro geral no upload:', error);
      
      // Fallback: converter para base64 com validação de tamanho
      console.log('🔄 [uploadImageToSupabase] Usando fallback base64...');
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          
          // Validar tamanho da URL base64 (máximo ~200KB para evitar problemas)
          if (result.length > 300000) {
            reject(new Error('Imagem muito grande para base64. Use uma imagem menor ou uma URL externa.'));
            return;
          }
          
          console.log('✅ [uploadImageToSupabase] Conversão base64 realizada:', {
            tamanho: result.length,
            tipo: result.substring(0, 50) + '...'
          });
          
          resolve(result);
        };
        reader.onerror = () => reject(new Error('Falha ao processar imagem'));
        reader.readAsDataURL(file);
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !authUser) {
      toast({
        title: "Erro",
        description: "Você precisa estar logado para criar uma comunidade.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.name.trim() || !formData.game_id) {
      toast({
        title: "Erro",
        description: "Por favor, preencha todos os campos obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    // ===== VALIDAÇÕES DE COMPRIMENTO =====
    if (formData.name.length > 100) {
      toast({
        title: "Erro",
        description: "Nome da comunidade não pode ter mais de 100 caracteres.",
        variant: "destructive",
      });
      return;
    }

    if (formData.description.length > 500) {
      toast({
        title: "Erro",
        description: "Descrição não pode ter mais de 500 caracteres.",
        variant: "destructive",
      });
      return;
    }
    
    if (formData.rules.length > 1000) {
      toast({
        title: "Erro",
        description: "Regras não podem ter mais de 1000 caracteres.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      let coverImageUrl = '';
      
      // Se há imagem para upload
      if (uploadMode === 'upload' && imageFile) {
        try {
          coverImageUrl = await uploadImageToSupabase(imageFile);
        } catch (uploadError: any) {
          console.error('❌ [CriarComunidade] Erro no upload:', uploadError);
          toast({
            title: "Erro no Upload",
            description: uploadError.message || "Falha ao fazer upload da imagem. Tente usar uma URL externa.",
            variant: "destructive",
          });
          return;
        }
      } else if (uploadMode === 'url' && imageUrl.trim()) {
        coverImageUrl = imageUrl.trim();
      }

      // Validar tamanho da URL (normal ou base64)
      if (coverImageUrl.length > 300000) {
        toast({
          title: "Erro", 
          description: "URL da imagem é muito longa. Use uma imagem menor ou uma URL externa menor.",
          variant: "destructive",
        });
        return;
      }

      // Validação adicional para URLs normais (não base64)
      if (coverImageUrl && !coverImageUrl.startsWith('data:') && coverImageUrl.length > 255) {
        toast({
          title: "Erro", 
          description: "URL da imagem externa é muito longa. Use uma URL menor.",
          variant: "destructive",
        });
        return;
      }

      const communityData = {
        name: formData.name.trim(),
        slug: generateSlug(formData.name.trim()),
        description: formData.description.trim() === '' ? null : formData.description.trim(),
        cover_image_url: coverImageUrl || null,
        game_id: formData.game_id,
        is_featured: formData.is_featured,
        rules: formData.rules.trim() || null
      };

      console.log('🎯 [CriarComunidade] Criando comunidade:', communityData);
      console.log('🎯 [CriarComunidade] Tamanhos dos campos:', {
        name: communityData.name.length,
        description: communityData.description.length,
        rules: communityData.rules.length,
        cover_image_url: communityData.cover_image_url.length
      });

      const newCommunity = await createCommunity(user.id, communityData);

      toast({
        title: "Sucesso!",
        description: "Comunidade criada com sucesso!",
      });

      // Redirecionar para a página da nova comunidade
      navigate(`/comunidades/${newCommunity.slug}`);

    } catch (error: any) {
      console.error('❌ [CriarComunidade] Erro:', error);
      
      // Dar mensagens mais específicas para erros comuns
      let errorMessage = error.message || "Falha ao criar comunidade. Tente novamente.";
      
      if (error.message?.includes('value too long')) {
        errorMessage = "Um dos campos está muito longo. Verifique o tamanho dos textos.";
      } else if (error.message?.includes('row-level security')) {
        errorMessage = "Você não tem permissão para fazer upload de imagens. Tente usar uma URL externa.";
      }
      
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F16] p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            onClick={() => navigate('/painel-mentor/comunidades')}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-[#1A2333]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-2xl font-bold text-white">Criar Nova Comunidade</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Coluna Principal */}
            <div className="lg:col-span-2 space-y-6">
              {/* Informações Básicas */}
              <Card className="bg-[#1A2333] border-[#222A35]">
                <CardHeader>
                  <CardTitle className="text-white">Informações Básicas</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-white">Nome da Comunidade *</Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Ex: CS2 Pro Players Brasil"
                      className="bg-[#0F1419] border-[#222A35] text-white"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="game_id" className="text-white">Jogo *</Label>
                    <Select value={formData.game_id} onValueChange={(value) => handleInputChange('game_id', value)}>
                      <SelectTrigger className="bg-[#0F1419] border-[#222A35] text-white">
                        <SelectValue placeholder="Selecione um jogo" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A2333] border-[#222A35]">
                        {games.map((game) => (
                          <SelectItem 
                            key={game.id} 
                            value={game.id}
                            className="text-white hover:bg-[#0F1419]"
                          >
                            {game.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="description" className="text-white">
                      Descrição
                      <span className="text-gray-400 text-sm ml-2">
                        ({formData.description.length}/500)
                      </span>
                    </Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 500) {
                          handleInputChange('description', value);
                        }
                      }}
                      placeholder="Descreva o objetivo e foco da sua comunidade... (máximo 500 caracteres)"
                      className={`bg-[#0F1419] border-[#222A35] text-white min-h-[100px] ${
                        formData.description.length > 450 ? 'border-yellow-500' : ''
                      } ${formData.description.length >= 500 ? 'border-red-500' : ''}`}
                    />
                    {formData.description.length > 450 && (
                      <p className={`text-sm mt-1 ${
                        formData.description.length >= 500 ? 'text-red-400' : 'text-yellow-400'
                      }`}>
                        {formData.description.length >= 500 
                          ? 'Limite de caracteres atingido!'
                          : `Você está próximo do limite (${500 - formData.description.length} caracteres restantes)`
                        }
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="rules" className="text-white">Regras da Comunidade</Label>
                    <Textarea
                      id="rules"
                      value={formData.rules}
                      onChange={(e) => handleInputChange('rules', e.target.value)}
                      placeholder="Defina as regras que os membros devem seguir..."
                      className="bg-[#0F1419] border-[#222A35] text-white min-h-[120px]"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Imagem de Capa */}
              <Card className="bg-[#1A2333] border-[#222A35]">
                <CardHeader>
                  <CardTitle className="text-white">Imagem de Capa</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Instruções detalhadas */}
                  <div className="bg-[#0F1419] border border-[#222A35] rounded-lg p-4">
                    <div className="flex items-start gap-2 mb-3">
                      <div className="w-5 h-5 rounded-full bg-[#00B6F0] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-black text-xs font-bold">i</span>
                      </div>
                      <div>
                        <h4 className="text-[#00B6F0] font-semibold text-sm mb-2">Instruções para Imagem de Capa</h4>
                        <ul className="text-gray-300 text-sm space-y-1">
                          <li className="flex items-center gap-2">
                            <span className="w-1 h-1 bg-[#00B6F0] rounded-full"></span>
                            <span><strong>Dimensões recomendadas:</strong> 1200x400 pixels (proporção 3:1)</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-1 h-1 bg-[#00B6F0] rounded-full"></span>
                            <span><strong>Formatos aceitos:</strong> JPG, PNG, WebP</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-1 h-1 bg-[#00B6F0] rounded-full"></span>
                            <span><strong>Tamanho máximo:</strong> 5MB</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-1 h-1 bg-[#00B6F0] rounded-full"></span>
                            <span><strong>Dica:</strong> Use imagens relacionadas ao jogo ou tema da comunidade</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-1 h-1 bg-[#00B6F0] rounded-full"></span>
                            <span><strong>Qualidade:</strong> Prefira imagens nítidas e com boa iluminação</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Modo de Upload */}
                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant={uploadMode === 'upload' ? 'default' : 'outline'}
                      onClick={() => setUploadMode('upload')}
                      className="flex-1"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload de Arquivo
                    </Button>
                    <Button
                      type="button"
                      variant={uploadMode === 'url' ? 'default' : 'outline'}
                      onClick={() => setUploadMode('url')}
                      className="flex-1"
                    >
                      <ImageIcon className="h-4 w-4 mr-2" />
                      Link da Imagem
                    </Button>
                  </div>

                  {uploadMode === 'upload' ? (
                    <div>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="image-upload"
                      />
                      <Label
                        htmlFor="image-upload"
                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#222A35] rounded-lg cursor-pointer hover:border-[#00B6F0] transition-colors"
                      >
                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-400">
                          Clique para selecionar uma imagem
                        </span>
                        <span className="text-xs text-gray-500 mt-1">
                          JPG, PNG ou WebP (máx. 5MB)
                        </span>
                        <span className="text-xs text-gray-500">
                          Recomendado: 1200x400px
                        </span>
                      </Label>
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="image-url" className="text-white">URL da Imagem</Label>
                      <Input
                        id="image-url"
                        type="url"
                        value={imageUrl}
                        onChange={(e) => handleImageUrlChange(e.target.value)}
                        placeholder="https://exemplo.com/imagem-capa-comunidade.jpg"
                        className="bg-[#0F1419] border-[#222A35] text-white"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Cole a URL de uma imagem hospedada externamente
                      </p>
                    </div>
                  )}

                  {/* Preview da Imagem */}
                  {imagePreview && (
                    <div className="relative">
                      <div className="relative h-48 rounded-lg overflow-hidden">
                        <img
                          src={imagePreview}
                          alt="Preview da capa"
                          className="w-full h-full object-cover"
                        />
                        <Button
                          type="button"
                          onClick={removeImage}
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-gray-400 mt-2">
                        Preview da imagem de capa da comunidade
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Configurações */}
              <Card className="bg-[#1A2333] border-[#222A35]">
                <CardHeader>
                  <CardTitle className="text-white">Configurações</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="is_featured" className="text-white">Comunidade em Destaque</Label>
                      <p className="text-sm text-gray-400">Aparecerá na lista de destaques</p>
                    </div>
                    <Switch
                      id="is_featured"
                      checked={formData.is_featured}
                      onCheckedChange={(checked) => handleInputChange('is_featured', checked)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Ações */}
              <Card className="bg-[#1A2333] border-[#222A35]">
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#00B6F0] text-black hover:bg-[#1AD3F9]"
                    >
                      {loading ? (
                        <>
                          <LoadingSpinner size="sm" className="mr-2" />
                          Criando...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Criar Comunidade
                        </>
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/painel-mentor/comunidades')}
                      className="w-full border-[#222A35] text-white hover:bg-[#0F1419]"
                    >
                      Cancelar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CriarComunidade; 